import React from 'react';
import i18next from "i18next";
import moment from "moment";
import { FiMoreHorizontal, FiPlus } from "react-icons/fi";
import { Dropdown } from "react-bootstrap";
import DataTable from "components/DataTable";
import Avatar from "components/Avatar";
import ShiftPatternShiftTag from "components/ShiftPatterns/ShiftPatternShiftTag";
import { convertFromUTC } from "utils/datetime";
import { getContrastColor } from "utils/utils";

class ShiftPatternEmployees extends React.Component {
	formatDate = (value) => {
		if (!value) return "-";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return "-";
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	getAssignment = (employee) => employee.employee_with_shift_pattern || null;

	getAssignmentStatus = (employee) => {
		const assignment = this.getAssignment(employee);
		if (!assignment) return "historical";

		const today = moment().startOf("day");
		const belongsToThisPattern = assignment.shift_pattern &&
			Number(assignment.shift_pattern.id) === Number(this.props.shiftPatternId);

		if (
			belongsToThisPattern &&
			assignment.start_day &&
			moment(assignment.start_day).isAfter(today, "day")
		) {
			return "scheduled";
		}

		if (
			!belongsToThisPattern ||
			(assignment.end_day && moment(assignment.end_day).isBefore(today, "day"))
		) {
			return "historical";
		}

		return null;
	};

	formatShiftIntervalLabels = (time_intervals) => {
		if (!time_intervals || time_intervals.length === 0) return [];
		return time_intervals.map((item) => {
			const start = convertFromUTC(item.start);
			const end = convertFromUTC(item.end);
			const short = (t) => {
				if (!t) return "";
				const [h, m] = t.split(":");
				return m === "00" ? h : `${h}:${m}`;
			};
			return `${short(start)}-${short(end)}h`;
		});
	};

	getStartDayShiftUbications = (assignment) => {
		if (!assignment) return [];
		if (
			assignment.shift_pattern_item &&
			Array.isArray(assignment.shift_pattern_item.shift_ubications)
		) {
			return assignment.shift_pattern_item.shift_ubications;
		}
		const startIndex =
			assignment.start_index !== undefined && assignment.start_index !== null
				? Number(assignment.start_index)
				: null;
		const items = assignment.shift_pattern?.items;
		if (
			startIndex === null ||
			!Array.isArray(items) ||
			!items[startIndex] ||
			!Array.isArray(items[startIndex].shift_ubications)
		) {
			return [];
		}
		return items[startIndex].shift_ubications;
	};

	renderStartDayCell = (employee) => {
		const assignment = this.getAssignment(employee);
		const startIndex = assignment?.start_index;
		if (startIndex === undefined || startIndex === null) return "-";
		const shiftUbications = this.getStartDayShiftUbications(assignment).filter(
			(su) => su && su.shift
		);
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
					maxWidth: "100%",
					overflow: "hidden",
				}}
			>
				<span
					style={{
						flexShrink: 0,
						fontWeight: 500,
					}}
				>
					{`${i18next.t("shiftPatterns.form.day")} ${Number(startIndex) + 1}`}
				</span>
				<div
					style={{
						display: "flex",
						gap: "6px",
						minWidth: 0,
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
					}}
					title={i18next.t("shiftPatterns.employees.startDayShifts")}
				>
					{shiftUbications.length > 0 ? (
						shiftUbications.map((su, i) => {
							const shift = su.shift;
							const textColor = getContrastColor(
								shift.background_color,
								"#55536b"
							);
							const title = `${shift.name}${
								shift.description ? ` - ${shift.description}` : ""
							}`;
							return (
								<div key={`${shift.id}-${i}`} style={{ maxWidth: "140px" }}>
									<ShiftPatternShiftTag
										className="sp-shift-tag--sm"
										name={shift.name}
										intervalLabels={this.formatShiftIntervalLabels(
											shift.time_intervals
										)}
										style={{
											backgroundColor: shift.background_color || undefined,
											borderColor: shift.border_color
												? shift.border_color
												: shift.background_color || undefined,
											color: textColor,
											width: "100%",
											maxWidth: "100%",
										}}
										title={title}
									/>
								</div>
							);
						})
					) : (
						<span
							style={{
								color: "#6c757d",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{i18next.t("shiftPatterns.employees.noShifts")}
						</span>
					)}
				</div>
			</div>
		);
	};

	renderUbicationCell = (employee) => {
		const ubication = this.getAssignment(employee)?.ubication;
		if (!ubication) return "-";
		const textColor = getContrastColor(ubication.background_color, "#55536b");
		const title = `${ubication.name}${
			ubication.description ? ` - ${ubication.description}` : ""
		}`;
		return (
			<div style={{ maxWidth: "180px", overflow: "hidden" }}>
				<ShiftPatternShiftTag
					className="sp-shift-tag--sm"
					name={ubication.name}
					intervalLabels={[]}
					style={{
						backgroundColor: ubication.background_color || undefined,
						borderColor: ubication.border_color
							? ubication.border_color
							: ubication.background_color || undefined,
						color: textColor,
						width: "100%",
						maxWidth: "100%",
					}}
					title={title}
				/>
			</div>
		);
	};

	getColumns = () => [
		{
			key: "employee",
			header: i18next.t("shiftPatterns.employees.headerEmployee"),
			sortable: false,
			render: (employee) => (
				<button
					type="button"
					className="btn-transparent btn-editable sp-employee-row-trigger"
					onClick={() => this.props.onEditEmployee(employee)}
				>
					<Avatar
						userId={employee.id}
						className="avatar-sm"
						alt={employee.name}
					/>
					<span className="sp-employee-row-trigger__name">
						{employee.name} {employee.surname}
					</span>
					{this.getAssignmentStatus(employee) && (
						<span className={`sp-assignment-status sp-assignment-status--${this.getAssignmentStatus(employee)}`}>
							{i18next.t(`shiftPatterns.employees.${this.getAssignmentStatus(employee)}`)}
						</span>
					)}
				</button>
			),
		},
		{
			key: "start_day",
			header: i18next.t("shiftPatterns.employees.startDay"),
			sortable: false,
			render: (employee) =>
				this.formatDate(this.getAssignment(employee)?.start_day),
		},
		{
			key: "start_index",
			header: i18next.t("shiftPatterns.employees.startBy"),
			sortable: false,
			render: this.renderStartDayCell,
		},
		{
			key: "ubication",
			header: i18next.t("common.location"),
			sortable: false,
			render: this.renderUbicationCell,
		},
	];

	renderEmployeeActions = (employee) => (
		<Dropdown align="end">
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						this.props.onEditEmployee(employee);
					}}
				>
					{i18next.t("shiftPatterns.employees.editAssignment")}
				</Dropdown.Item>
				<Dropdown.Item
					as="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						this.props.onCloneEmployee(employee);
					}}
				>
					{i18next.t("shiftPatterns.employees.cloneAssignment")}
				</Dropdown.Item>
				<Dropdown.Item
					as="button"
					className="text-danger"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						this.props.onRemoveEmployee(employee);
					}}
				>
					{i18next.t("common.delete")}
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	render() {
		const { employees, onAddEmployee } = this.props;

		if (!employees) {
			return null;
		}

		return (
			<DataTable
				tableClassName="table-shift-patterns table-hover sp-shift-pattern-employees-table"
				tableStyle={{ backgroundColor: "#ffffff" }}
				columns={this.getColumns()}
				rows={employees}
				rowKey={(row) => row.id}
				rowClassName={(row) =>
					this.getAssignmentStatus(row) ? "sp-employee-inactive" : undefined
				}
				emptyMessage={i18next.t("shiftPatterns.employees.noEmployees")}
				showHeaderSortAffix={false}
				actionsColumn={{
					header: (
						<button
							type="button"
							title={i18next.t("shiftPatterns.employees.addEmployee")}
							onClick={onAddEmployee}
							className="btn btn-new"
						>
							<FiPlus />
						</button>
					),
					headerClassName: "td-actions text-end",
					className: "td-actions",
					render: this.renderEmployeeActions,
				}}
			/>
		);
	}
}

export default ShiftPatternEmployees;
